import { ApolloError } from "@apollo/client";
import { useLiveQuery } from "dexie-react-hooks";
import { useNavigate } from "react-router-dom";
import { casesActions, db } from "configs";
import { generateId, useAuth, useDispatch, useToast } from "duck";
import {
  CreateMedicalCaseMutationVariables,
  MedicalCasesDocument,
  useCreateMedicalCaseMutation
} from "requests";
import { FormValues } from "./types";

export const useCreateCase = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [createCase] = useCreateMedicalCaseMutation();

  return ({ caseName, doctor }: FormValues) => {
    const input: CreateMedicalCaseMutationVariables["input"] = {
      name: caseName,
      client: {
        patient: {
          name: caseName,
          ...(doctor
            ? {
                dropOff: { evaluateTestAndTreat: true, doctor },
                diagnosticTesting: { doctor, reviewedByDvm: false }
              }
            : {})
        }
      }
    };

    return createCase({
      variables: { input },
      refetchQueries: [{ query: MedicalCasesDocument }]
    })
      .then(data => {
        navigate(
          `/cases/${data?.data?.createMedicalCase?.medicalCase?._id}/check-in`
        );
        toast({
          status: "success",
          description: `Case ${caseName} is created successfully.`
        });
      })
      .catch((error: ApolloError) => {
        const isAccessDenied =
          error.graphQLErrors?.[0]?.extensions?.code === "access_denied";
        if (isAccessDenied) {
          toast({ status: "error", description: "Access denied" });
        }
      });
  };
};

export const useCreateOfflineCase = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [{ user }] = useAuth();
  const dispatch = useDispatch();

  const authorizations = useLiveQuery(
    () => db.authorizations.toArray(),
    [],
    []
  );
  const disclaimerProcedures = useLiveQuery(
    () => db.disclaimerProcedures.toArray(),
    [],
    []
  );
  const disclaimerText = useLiveQuery(() =>
    db.disclaimerText.get({ key: "text" })
  );

  return ({ caseName, doctor }: FormValues) => {
    const id = Date.now();
    dispatch(
      casesActions.initOffline({
        userId: user._id,
        name: caseName,
        doctor,
        id,
        authorizations: authorizations.map(auth => ({
          condition: auth.condition,
          selected: false,
          fakeId: generateId()
        })),
        disclaimerProcedures: disclaimerProcedures.map(procedure => ({
          description: procedure.description,
          selected: false,
          title: procedure.title,
          fakeId: generateId()
        })),
        disclaimerText: disclaimerText?.value || ""
      })
    );
    navigate(`/cases/${id}/check-in`);
    toast({
      status: "success",
      description: `Case ${caseName} is created successfully.`
    });
  };
};
